import { createContext, useContext } from 'react';

/**
 * Application roles. Entra App Roles GATE access; `viewer` / `editor` come
 * from our own `brain_user_role` table, never from a token claim
 * (CONVENTIONS.md §4.6).
 */
export type AppRole = 'viewer' | 'editor';

export interface AuthUser {
  /** Entra `oid`. */
  id: string;
  email: string;
  displayName: string;
  role: AppRole;
}

/**
 * Why the app is not showing the dashboard, when it is not.
 *
 * `denied` and `failed` are deliberately different states. A 403 means the
 * person is signed in and still not entitled — sending them back to Entra
 * would loop them through a successful login into the same refusal, so the
 * screen has to explain who to ask instead. A transport failure is a retry.
 */
export type AuthPhase = 'resolving' | 'ready' | 'redirecting' | 'denied' | 'failed';

export interface AuthState {
  user: AuthUser | null;
  /** True once the session has been resolved (successfully or not). */
  isResolved: boolean;
  /** Editors alone see admin screens and publish snapshots (rule #8). */
  isEditor: boolean;
  phase: AuthPhase;
  /** Human-readable reason for `denied` / `failed`. Empty otherwise. */
  message: string;
  /**
   * End the session at the BFF, then at Entra.
   *
   * On the context rather than as a loose helper because meeting mode runs on
   * a shared room screen: "sign out" there has to end the Microsoft session
   * too, and every call site must get that same behaviour.
   */
  signOut: () => void;
}

export const AUTH_DEFAULT: AuthState = {
  user: null,
  isResolved: false,
  isEditor: false,
  phase: 'resolving',
  message: '',
  signOut: () => undefined,
};

export const AuthContext = createContext<AuthState>(AUTH_DEFAULT);

/** Read the current session. One Context, no store (CONVENTIONS.md §4.4). */
export function useAuth(): AuthState {
  return useContext(AuthContext);
}
